import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface BreadcrumbContextType {
  /** Label personnalisé pour le DERNIER segment du breadcrumb. */
  lastLabel: string | null;
  setLastLabel: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [lastLabel, setLastLabel] = useState<string | null>(null);

  return (
    <BreadcrumbContext.Provider value={{ lastLabel, setLastLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return ctx;
}

/**
 * Hook utilitaire : surcharge le label du dernier segment du breadcrumb pour
 * la page courante. Passe `null` ou `undefined` pour ne rien surcharger.
 * Le label est automatiquement réinitialisé quand le composant se démonte.
 */
export function useBreadcrumbLabel(label: string | null | undefined) {
  const { setLastLabel } = useBreadcrumb();
  const stableSet = useCallback(setLastLabel, [setLastLabel]);

  useEffect(() => {
    if (label) {
      stableSet(label);
    }
    return () => {
      stableSet(null);
    };
  }, [label, stableSet]);
}
